import { useContext, useEffect, useReducer, useMemo, useState } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import BatchStudentRatings from "../components/teacher/BatchStudentRatings.jsx";
import BreadCrumb from "../components/breadCrumb/BreadCrumb.jsx";
import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao.jsx";
import EprSummary from "../components/teacher/EprSummary.jsx";
import GradingProfilesReducer from "../utils/GradingProfileReducer.jsx";
import GroupShare from "../components/teacher/GroupShare.jsx";
import LoadingSvg from "../components/loadingSvg/LoadingSvg.jsx";
import ManageDifferentiation from "../components/teacher/ManageDifferentiation.jsx";
import ManageRoster from "../components/teacher/ManageRoster.jsx";
import SingleStudentRatings from "../components/teacher/singleStudentPage/SingleStudentRatings.jsx";
import StudentSearch from "./StudentSearch.jsx";
import TeacherSidebarMenu from "../components/sidebar/TeacherSidebarMenu.jsx";
import RbA from "../components/rba/RbA.jsx";
import UserDao from "../dao/UserDao.jsx";

import { GlobalContext } from "../components/contextProvider/ContextProvider.jsx";
import { useEnsureSchoolTemplatesForLocation } from "../components/contextProvider/ContextSetter.jsx";
import { instructionalAreaToGrade } from "../utils/SchoolAdmin/translations.jsx";
import { VALID_INSTRUCTIONAL_AREA_KEYS } from "../utils/SchoolAdmin/constants.js";
import { isUserDistrictAdmin, isUserPk12 } from "../utils/isUser.jsx";

import "../styles/TeacherHome.scss";
import "../styles/TeacherHomeForAdmin.scss";
import "react-toastify/dist/ReactToastify.css";

/**
 * TeacherHomeForAdmin component serves as the dashboard for admins to view a teacher's
 * grading profiles, rosters, and summary views.
 *
 * @component
 * @returns {JSX.Element}
 */
const TeacherHomeForAdmin = () => {
    // URL params: /home/teacher-view/:teacherGuid/:locationKey/:timePeriodKey/:section...
    const { teacherGuid, locationKey, timePeriodKey, studentNumber } = useParams();
    const { pathname } = useLocation();

    const { state: globalState, dispatch: globalDispatch } = useContext(GlobalContext);
    const { currentTimePeriodDto, schoolYearDto, token, schoolTemplatesByLocation, timePeriodDto, userDetails } =
        globalState || {};

    const ensureTemplates = useEnsureSchoolTemplatesForLocation();

    // The base path for all sub-navigation in this "mimic" mode
    const basePath = `/home/teacher-view/${teacherGuid}/${locationKey}/${timePeriodKey}`;

    const initialState = {
        gradingProfiles: [],
        selectedEpr: null,
        fetchProfileFlag: true,
        loader: true,
        navCollapsed: false,
        selectedLink: "HOME/SUMMARY"
    };
    const [state, dispatch] = useReducer(GradingProfilesReducer, initialState);

    const [teacherDetails, setTeacherDetails] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(null);

    useEffect(() => {
        if (!userDetails) return;

        if (isUserDistrictAdmin(userDetails) || isUserPk12(userDetails)) {
            setIsAuthorized(true);
            return;
        }

        const adminLocationKey =
            userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey;
        const authorized = String(adminLocationKey) === String(locationKey);

        setIsAuthorized(authorized);
    }, [userDetails, locationKey]);

    // once teacher details load, verify the teacher actually belongs to the locationKey in the URL
    useEffect(() => {
        if (!teacherDetails || !userDetails) return;
        if (isAuthorized === false) return; // already blocked, no need to re-check
        if (isUserDistrictAdmin(userDetails) || isUserPk12(userDetails)) return; // district-level roles bypass

        const teacherLocationKey =
            teacherDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey;

        if (!teacherLocationKey) {
            return;
        }

        const authorized = String(teacherLocationKey) === String(locationKey);

        if (!authorized) setIsAuthorized(false);
    }, [teacherDetails, userDetails, locationKey, isAuthorized]);

    const adminBreadcrumbs = useMemo(() => {
        const crumbs = [{ label: "Home", path: basePath }];

        const relativePath = pathname.substring(basePath.length);
        if (relativePath) {
            const splitPath = relativePath.split("/").filter(Boolean);
            const segments = splitPath
                .map((seg, index, array) => {
                    const rawLabel = seg.toLowerCase();

                    // Skip any segments that are just numeric IDs
                    if (!Number.isNaN(Number(rawLabel))) {
                        return null;
                    }

                    let segmentPath = "";
                    if (rawLabel === "summary") {
                        segmentPath = `${basePath}/summary`;
                        return { label: "Report Card Summary", path: segmentPath };
                    }
                    if (rawLabel === "roster") {
                        segmentPath = `${basePath}/summary/roster`;
                        return { label: "Manage Roster", path: segmentPath };
                    }
                    if (rawLabel === "share") {
                        segmentPath = `${basePath}/summary/share`;
                        return { label: "Manage Shares", path: segmentPath };
                    }
                    if (rawLabel === "input-ratings") {
                        segmentPath = `${basePath}/summary/input-ratings`;
                        return { label: "Input Ratings", path: segmentPath };
                    }
                    if (rawLabel === "differentiation") {
                        segmentPath = `${basePath}/summary/differentiation`;
                        return { label: "Manage Differentiation", path: segmentPath };
                    }
                    if (rawLabel === "student-search") {
                        segmentPath = `${basePath}/student-search`;
                        return { label: "Student Search", path: segmentPath };
                    }
                    if (rawLabel === "student" && studentNumber) {
                        segmentPath = `${basePath}/summary/input-ratings/student/${studentNumber}`;
                        return { label: `Student`, path: segmentPath };
                    }
                    if (rawLabel === "homeroom") {
                        segmentPath = `${basePath}/summary/input-ratings/homeroom`;
                        const nextSeg = array[index + 1];
                        if (nextSeg && !Number.isNaN(Number(nextSeg))) {
                            segmentPath += `/${nextSeg}`;
                        }
                        return { label: "Homeroom", path: segmentPath };
                    }
                    if (rawLabel === "groups") {
                        segmentPath = `${basePath}/summary/input-ratings/groups`;
                        const nextSeg = array[index + 1];
                        if (nextSeg && !Number.isNaN(Number(nextSeg))) {
                            segmentPath += `/${nextSeg}`;
                        }
                        return { label: "Groups", path: segmentPath };
                    }
                    return { label: rawLabel, path: `${basePath}/${rawLabel}` };
                })
                .filter(Boolean);
            crumbs.push(...segments);
        }

        return crumbs;
    }, [pathname, basePath, studentNumber]);

    // Sync the requested timePeriodKey (from URL) to the global viewTimePeriodDto
    // This allows the initial load to show the correct semester, but lets the user switch tabs later.
    useEffect(() => {
        if (timePeriodKey && timePeriodDto && globalDispatch) {
            const requestedDto = timePeriodDto.find((tp) => Number(tp.key) === Number(timePeriodKey));
            if (requestedDto) {
                globalDispatch({ type: "ViewTimePeriodDto", viewTimePeriodDto: requestedDto });
            }
        }
    }, [timePeriodKey, timePeriodDto, globalDispatch]);

    useEffect(() => {
        return () => {
            globalDispatch({ type: "ViewTimePeriodDto", viewTimePeriodDto: null });
        };
    }, [globalDispatch]);

    // Ensure templates for this specific location are loaded
    useEffect(() => {
        if (locationKey) {
            ensureTemplates({ locationKey });
        }
    }, [locationKey, ensureTemplates]);

    // Retrieve the specific school templates from the cache
    const schoolLevelForLocation = schoolTemplatesByLocation?.[locationKey];

    const schoolLevelTemplateDto = useMemo(() => {
        return schoolLevelForLocation || null;
    }, [schoolLevelForLocation]);

    /**
     * Retrieves the list of released grades for the current time period.
     */
    const releasedGrades = useMemo(() => {
        if (!schoolLevelTemplateDto) return [];
        const currentTimePeriod = currentTimePeriodDto?.name === "1st Semester" ? "firstSemester" : "secondSemester";
        return (
            schoolLevelTemplateDto[currentTimePeriod]?.gradeToSubjects.filter((grade) => grade.templatesReleased) || []
        );
    }, [schoolLevelTemplateDto, currentTimePeriodDto]);

    /**
     * Determines the teacher's assigned grade if it has been released.
     * Note: for Admin view, we might not know the teacher's Instructional Area easily unless we fetched their user details.
     * For now, we'll leave this null or try to infer if needed.
     */
    const teacherGradeIfReleased = useMemo(() => {
        if (
            !teacherDetails ||
            (!teacherDetails.instructionalAreas &&
                !teacherDetails.userAttributeDto?.userAttributeMap?.INSTRUCTIONAL_AREA_KEY)
        ) {
            return null;
        }

        let conceptualGrade = null;

        // Check explicit instructional area key first
        const iAkey = teacherDetails.userAttributeDto?.userAttributeMap?.INSTRUCTIONAL_AREA_KEY;
        if (iAkey && VALID_INSTRUCTIONAL_AREA_KEYS.has(iAkey)) {
            conceptualGrade = instructionalAreaToGrade[iAkey];
        }

        // Check instructionalAreas array as a fallback
        if (!conceptualGrade && teacherDetails.instructionalAreas && Array.isArray(teacherDetails.instructionalAreas)) {
            const foundIa = teacherDetails.instructionalAreas.find((ia) => VALID_INSTRUCTIONAL_AREA_KEYS.has(ia.key));
            if (foundIa) {
                conceptualGrade = instructionalAreaToGrade[foundIa.key];
            }
        }

        if (conceptualGrade) {
            return { gradeLevel: conceptualGrade };
        }

        return null;
    }, [teacherDetails]);

    /**
     * Updates the selected navigation link based on the current route.
     * Custom logic for the /home/teacher-view/GUID/LOC prefix.
     */
    useEffect(() => {
        // pathname example: /home/teacher-view/123/456/summary/roster
        // We want to extract: HOME/SUMMARY/ROSTER
        if (pathname.startsWith(basePath)) {
            let relative = pathname.substring(basePath.length);
            if (relative.startsWith("/")) relative = relative.substring(1);

            let segments = relative
                .split("/")
                .filter((seg) => seg && Number.isNaN(Number(seg)))
                .map((seg) => seg.toUpperCase());

            let mockPath = segments.join("/");

            if (mockPath.includes("SUMMARY/INPUT-RATINGS/STUDENT")) {
                mockPath = "SUMMARY/INPUT-RATINGS/STUDENT";
            }

            if (mockPath) {
                dispatch({ type: "SET_SELECTED_LINK", payload: `HOME/${mockPath}` });
            } else {
                dispatch({ type: "SET_SELECTED_LINK", payload: "HOME" });
            }
        }
    }, [pathname, basePath]);

    /**
     * Fetches grading profiles for the TARGET TEACHER (teacherGuid).
     */
    useEffect(() => {
        if (teacherGuid && schoolYearDto && state.fetchProfileFlag) {
            const options = {
                action: "getGradingProfile",
                guid: teacherGuid, // Use the target teacher's GUID
                schoolYearKey: schoolYearDto?.key,
                token
            };

            dispatch({ type: "SET_LOADER", payload: true });

            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response) {
                        dispatch({ type: "SET_FETCH_FLAG", payload: false });
                        dispatch({ type: "SET_PROFILES", payload: response.data.payload || [] });
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    dispatch({ type: "SET_LOADER", payload: false });
                });
        }
    }, [schoolYearDto, state.fetchProfileFlag, token, teacherGuid]);

    /**
     * Fetches the teacher's user details and instructional area to get their username for API calls.
     */
    useEffect(() => {
        if (teacherGuid && token && !teacherDetails) {
            const fetchDetails = async () => {
                try {
                    const userDetailsResponse = await UserDao({
                        action: "userDetailsByGuidRead",
                        guid: teacherGuid,
                        token
                    });

                    const instructionalAreaResponse = await UserDao({
                        action: "userInstructionalAreaRead",
                        guid: teacherGuid,
                        token
                    });

                    if (userDetailsResponse?.data?.payload) {
                        const mergedDetails = { ...userDetailsResponse.data.payload };
                        if (instructionalAreaResponse?.data?.payload) {
                            mergedDetails.instructionalAreas = instructionalAreaResponse.data.payload;
                        }
                        setTeacherDetails(mergedDetails);
                    }
                } catch (error) {
                    console.error("Failed to fetch teacher details or instructional area:", error);
                }
            };
            fetchDetails();
        }
    }, [teacherGuid, token, teacherDetails]);

    const adminRoles = ["EPR_ADMIN", "PK12", "PRINCIPAL", "ASSISTANT_PRINCIPAL"];

    if (isAuthorized === false) return <Navigate to="/notFound" />;

    return (
        <RbA allowedRoles={adminRoles} redirect="/notFound">
            <div className="gutter-95" style={{ padding: "0px 0px" }}>
                <div className="golden-rod golden-rod--viewport-wide">
                    <strong>Viewing Teacher</strong> -{" "}
                    {teacherDetails?.displayName ? teacherDetails.displayName : `GUID: ${teacherGuid}`}
                </div>
                <ToastContainer style={{ width: "50%" }} />
                <BreadCrumb style={{ marginTop: "-10px" }} labelObj={adminBreadcrumbs} />

                <main style={{ display: "flex" }}>
                    <TeacherSidebarMenu
                        dispatch={dispatch}
                        state={state}
                        basePath={basePath}
                        teacherGuid={teacherGuid}
                    />

                    {state.selectedLink === "HOME" && !state.loader && (
                        <div className="teacher-home-wrapper">
                            {/* We can hide or simplify the Welcome message for Admin view */}
                            <section className="section-container top">
                                <div className="sub-section-title">Teacher Dashboard</div>
                                <div className="sub-section-line" />
                                <div style={{ color: "white", fontWeight: 450 }}>
                                    You are viewing the dashboard for this teacher. Use the menu to inspect their Report
                                    Cards.
                                </div>
                            </section>
                        </div>
                    )}
                    {state.selectedLink.includes("HOME/STUDENT-SEARCH") && !state.loader && <StudentSearch />}

                    {state.selectedLink === "HOME/SUMMARY" && !state.loader && (
                        <EprSummary
                            dispatch={dispatch}
                            releasedGrades={releasedGrades}
                            state={state}
                            teacherGradeIfReleased={teacherGradeIfReleased}
                            teacherGuid={teacherGuid} // Pass target teacher GUID
                            schoolLevelTemplateDto={schoolLevelTemplateDto}
                            basePath={basePath}
                            subjectUser={teacherDetails}
                        />
                    )}
                    {state.selectedLink.includes("HOME/SUMMARY/ROSTER") && !state.loader && teacherDetails && (
                        <ManageRoster
                            dispatch={dispatch}
                            path={pathname}
                            state={state}
                            basePath={basePath}
                            ownerUsername={teacherDetails.username}
                            schoolLevelTemplateDto={schoolLevelTemplateDto}
                        />
                    )}
                    {state.selectedLink.includes("HOME/SUMMARY/SHARE") && !state.loader && (
                        <GroupShare dispatch={dispatch} state={state} basePath={basePath} />
                    )}
                    {state.selectedLink.includes("HOME/SUMMARY/DIFFERENTIATION") && !state.loader && (
                        <ManageDifferentiation
                            dispatch={dispatch}
                            state={state}
                            teacherGuid={teacherGuid}
                            schoolLevelTemplateDto={schoolLevelTemplateDto}
                        />
                    )}
                    {state.selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS") &&
                        (studentNumber ? (
                            <SingleStudentRatings
                                dispatch={dispatch}
                                state={state}
                                basePath={basePath}
                                overrideTeacherGuid={teacherGuid}
                            />
                        ) : (
                            <BatchStudentRatings
                                dispatch={dispatch}
                                state={state}
                                basePath={basePath}
                                teacherGuid={teacherGuid}
                            />
                        ))}
                </main>
                {state.loader && <LoadingSvg />}
            </div>
        </RbA>
    );
};

export default TeacherHomeForAdmin;
